import React, { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableContainer,
} from '@mui/material';
import { format, isSameWeek } from 'date-fns';
import {
  PurchaseOrderItem,
  PurchaseOrderItemStatus,
} from '@domain/purchasing';
import EmptyState from '@shared/ui/common/EmptyState';
import PurchaseOrderItemRow from './PurchaseOrderItemRow';
import {
  PAGE_SIZE,
  StatusMilestone,
  getDisplayMilestone,
  getFinalEta,
  getStatusEndDate,
} from './utils';

interface PurchaseOrderItemListProps {
  items: PurchaseOrderItem[];
  page: number;
  isLoading: boolean;
  today: Date;
  selectedWeekStart: Date;
  selectedWeekEnd: Date;
  currentWeekStart: Date;
  updatingId: string | null;
  onPageChange: (page: number) => void;
  onOpen: (item: PurchaseOrderItem) => void;
  onConfirm: (item: PurchaseOrderItem, milestone: StatusMilestone) => void;
  onDelayed: (item: PurchaseOrderItem) => void;
  onAddShipment: (item: PurchaseOrderItem) => void;
  onChangeStatus: (
    item: PurchaseOrderItem,
    status: PurchaseOrderItemStatus
  ) => void;
}

const PurchaseOrderItemList: React.FC<PurchaseOrderItemListProps> = ({
  items,
  page,
  isLoading,
  today,
  selectedWeekStart,
  selectedWeekEnd,
  currentWeekStart,
  updatingId,
  onPageChange,
  onOpen,
  onConfirm,
  onDelayed,
  onAddShipment,
  onChangeStatus,
}) => {
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pagedItems = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page, totalPages]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={280}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: 200 }}>
        <EmptyState
          title={`No updates ${
            isSameWeek(selectedWeekStart, currentWeekStart, {
              weekStartsOn: 1,
            })
              ? 'this week'
              : `in week ${format(selectedWeekStart, 'w')}`
          }`}
          description="There are no new, updated or completed purchase orders this week."
        />
      </Box>
    );
  }

  return (
    <>
      <TableContainer sx={{ width: '100%' }}>
        <Table sx={{ width: '100%' }}>
          <TableBody>
            {pagedItems.map((item) => {
              const milestone: StatusMilestone =
                getDisplayMilestone(
                  item,
                  selectedWeekStart,
                  selectedWeekEnd
                ) ?? {
                  date: getStatusEndDate(item) ?? getFinalEta(item)!,
                  kind: 'eta',
                };
              return (
                <PurchaseOrderItemRow
                  key={
                    item.id ??
                    `${item.product.id}-${milestone.date.toISOString()}`
                  }
                  item={item}
                  milestone={milestone}
                  today={today}
                  isUpdating={updatingId === item.id}
                  onOpen={onOpen}
                  onConfirm={onConfirm}
                  onDelayed={onDelayed}
                  onAddShipment={onAddShipment}
                  onChangeStatus={onChangeStatus}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {items.length > PAGE_SIZE && (
        <Stack
          p={2}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Pagination
            count={totalPages}
            page={Math.min(page, totalPages)}
            onChange={(_, value) => onPageChange(value)}
            color="primary"
            size="large"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </>
  );
};

export default PurchaseOrderItemList;
